import { useApolloClient, type InternalRefetchQueriesInclude } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type {
  DeferredConnectResult,
  DeferredConnectStatus,
  DeferredConnectStrings,
  DeferredConnectTarget,
  DeferredConnectToaster,
} from '@arthelper/ui';

import { SEND_CONNECTION_REQUEST_MUTATION } from '~/graphql/connection';

/** The client's sonner toaster, shaped for useDeferredConnect. */
export const connectToaster: DeferredConnectToaster = {
  show: (message, options) => {
    return toast(message, options);
  },
  dismiss: (id) => {
    toast.dismiss(id);
  },
  success: (message) => {
    toast.success(message);
  },
  error: (message) => {
    toast.error(message);
  },
};

export interface ConnectSendResult extends DeferredConnectResult {
  connectionId: string | null;
}

/** sendConnectionRequest only ever returns a pending or accepted row. */
function narrowStatus(status: unknown): DeferredConnectStatus {
  if (status === 'ACCEPTED') {
    return 'ACCEPTED';
  }
  return 'PENDING';
}

function firstName(name: string) {
  return name.split(/\s+/)[0] || name;
}

export function useConnectStrings(): DeferredConnectStrings {
  const { t } = useTranslation('social');
  return {
    pending: (target: DeferredConnectTarget) => {
      return t('connection.toast-pending', { name: firstName(target.displayName) });
    },
    addNote: t('connection.add-note'),
    cancel: t('connection.cancel'),
    sent: t('connection.toast-sent'),
    connected: t('connection.toast-connected'),
    failed: t('connection.toast-failed'),
  };
}

/**
 * SEND_CONNECTION_REQUEST_MUTATION as a useDeferredConnect `send`, resolving the
 * new row's id alongside its status.
 */
export function useConnectSend(refetchQueries?: InternalRefetchQueriesInclude) {
  const client = useApolloClient();

  return async (target: DeferredConnectTarget, note: string | null): Promise<ConnectSendResult> => {
    try {
      const { data } = await client.mutate({
        mutation: SEND_CONNECTION_REQUEST_MUTATION,
        variables: { input: { userId: target.userId, ...(note ? { message: note } : {}) } },
        ...(refetchQueries ? { refetchQueries } : {}),
      });
      const connection = data?.sendConnectionRequest;
      return { status: narrowStatus(connection?.status), connectionId: connection?.id ?? null };
    } catch (error) {
      console.error('Send connection request failed:', error);
      throw error;
    }
  };
}
